import { defineStore } from "pinia";
import { ref } from "vue";
import { Timestamp, type FirestoreError } from "firebase/firestore";
import { firebaseMessaging } from "@/helpers/firebase/firebase";
import { getToken } from "firebase/messaging";
import * as Sentry from "@sentry/vue";
import { isRunningInFlutter } from "@/helpers/flutter/capabilities";
import { updateUserDoc } from "@/helpers/firestore/UserService";
import { upsertUserPushTokensForGroup } from "@/helpers/firestore/GroupPushSettingsService";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { handleFirestoreException } from "@/helpers/exceptions/handler";

type MessageToken = {
  token: string;
  updated_at: Date;
};

export const useNotificationsStore = defineStore("notifications", () => {
  // State
  const hasFetched = ref(false);
  const hasFcmToken = ref(false);
  const hasMobileNotificationPermission = ref(false);

  // Actions
  function setHasFetched(value: boolean) {
    hasFetched.value = value;
  }

  function setHasFcmToken(value: boolean) {
    hasFcmToken.value = value;
  }

  function setHasMobileNotificationPermission(value: boolean) {
    hasMobileNotificationPermission.value = value;
  }

  function resetNotifications() {
    hasFetched.value = false;
    hasFcmToken.value = false;
    hasMobileNotificationPermission.value = false;
  }

  /**
   * Registers a messaging token for push notifications
   * @param token - Optional token to register. If not provided, will fetch from Firebase
   */
  async function registerMessageToken(token?: string): Promise<void> {
    const authStore = useAuthStore();
    const userStore = useUserStore();

    const messageTokens = userStore.userData?.message_tokens || [];
    const groups = userStore.userData?.groups;

    if (!groups) {
      return;
    }

    // Fetch token from Firebase if not provided and not in Flutter
    if (!token && !isRunningInFlutter() && firebaseMessaging) {
      token = await getToken(firebaseMessaging, {
        vapidKey: import.meta.env.VITE_WEB_PUSH_TOKEN,
      });
      setHasFetched(true);
      if (!token) {
        return;
      }
    }

    if (!token) {
      return;
    }

    setHasFcmToken(true);

    // Add the token to the messageTokens array
    messageTokens.push({ token, updated_at: new Date() });

    // Convert Firestore timestamps to Date objects
    for (let i = 0; i < messageTokens.length; i++) {
      const item = messageTokens[i];
      if (!item) continue;

      const updatedAt = item.updated_at;
      if (
        updatedAt &&
        typeof updatedAt === "object" &&
        "nanoseconds" in updatedAt &&
        "seconds" in updatedAt
      ) {
        const ts = updatedAt as { seconds: number; nanoseconds: number };
        item.updated_at = new Timestamp(ts.seconds, ts.nanoseconds).toDate();
      } else if (
        updatedAt &&
        typeof updatedAt === "object" &&
        "toDate" in updatedAt
      ) {
        const ts = updatedAt as { toDate: () => Date };
        item.updated_at = ts.toDate();
      } else {
        // The updated_at is a date string
        item.updated_at = new Date(updatedAt);
      }
    }

    // Remove duplicate tokens (keep the most recent)
    const tokenObject: Record<string, Date> = {};
    messageTokens.forEach(({ token: t, updated_at }) => {
      const date =
        updated_at instanceof Date ? updated_at : new Date(updated_at);
      if (t && (!tokenObject[t] || tokenObject[t] < date)) {
        tokenObject[t] = date;
      }
    });

    // Create the cleanTokens array from the unique tokens
    let cleanTokens: MessageToken[] = Object.entries(tokenObject).map(
      ([token, updated_at]) => ({
        token,
        updated_at,
      }),
    );

    // Only keep newest 5 tokens
    if (cleanTokens.length > 5) {
      cleanTokens.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
      cleanTokens = cleanTokens.slice(0, 5);
    }

    // Update user document with clean tokens
    const uuid = authStore.authUuid;
    if (!uuid) {
      return;
    }

    try {
      await updateUserDoc(uuid, {
        message_tokens: cleanTokens,
      });
      userStore.setPartialUserData({ message_tokens: cleanTokens });
    } catch (e) {
      Sentry.captureException(e);
    }

    // Update tokens in each group's settings
    for (const groupId of groups) {
      try {
        await upsertUserPushTokensForGroup({
          groupId,
          userId: uuid,
          tokens: cleanTokens,
          language:
            userStore.userData?.language ||
            localStorage.getItem("locale") ||
            undefined,
        });
      } catch (e) {
        handleFirestoreException({ e: e as FirestoreError });
      }
    }
  }

  return {
    hasFetched,
    hasFcmToken,
    hasMobileNotificationPermission,
    setHasFetched,
    setHasFcmToken,
    setHasMobileNotificationPermission,
    resetNotifications,
    registerMessageToken,
  };
});
